import { useQuery } from '@tanstack/react-query';
import { fetchSearchResults } from './useSearch';
import { stripProfessionalTitle } from '@/lib/professionalName';

interface Specialty {
  ESPECIALIDAD: string;
  CODIGO: string;
}

interface UseProfessionalSpecialtiesParams {
  plan: string;
  /** Nombre del profesional seleccionado (puede venir con título; se limpia). */
  professionalName?: string;
  enabled?: boolean;
}

/**
 * Especialidades del profesional seleccionado.
 *
 * Deriva las especialidades de los propios resultados de búsqueda del profesional
 * (campo ESPECIALIDAD) para acotar el dropdown de Especialidad a lo que esa
 * persona efectivamente atiende, en lugar de la lista genérica del plan.
 * Reusa el mismo origen que la búsqueda final (`/api/search`).
 */
export function useProfessionalSpecialties({
  plan,
  professionalName,
  enabled = true,
}: UseProfessionalSpecialtiesParams) {
  const name = stripProfessionalTitle(professionalName);

  return useQuery({
    queryKey: ['professional-specialties', plan, name],
    queryFn: async (): Promise<Specialty[]> => {
      const results = await fetchSearchResults({
        plan,
        searchTypeCode: 'C1', // "Nombre del profesional"
        vTexto: name,
      });

      // Especialidades únicas preservando el orden de aparición.
      const seen = new Set<string>();
      const specialties: Specialty[] = [];
      for (const p of results.professionals) {
        const esp = p.ESPECIALIDAD?.trim();
        if (esp && !seen.has(esp.toLowerCase())) {
          seen.add(esp.toLowerCase());
          specialties.push({ ESPECIALIDAD: esp, CODIGO: '' });
        }
      }
      return specialties;
    },
    enabled: enabled && !!plan && name.length > 0,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });
}
